import { http } from './request';
import { ECOMMERCE_ACCOUNT_URLS as API_URLS } from './urls';
import type { KnownEcommerceAuthStatus } from '../../common/enums/ecommerceAuthStatus';

export type EcommercePlatformType = 'DOUYIN';
export type EcommerceAccountType = 'SHOP' | 'DOUYIN_ACCOUNT';
export type EcommerceBusinessType = 'SELF_OPERATED' | 'BUYING';
export const ECOMMERCE_ACCOUNT_SESSION_STATUS = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
} as const;
export type EcommerceAccountSessionStatus =
  typeof ECOMMERCE_ACCOUNT_SESSION_STATUS[keyof typeof ECOMMERCE_ACCOUNT_SESSION_STATUS];
export const DOUYIN_CREATOR_BROWSER_STATE_ORIGIN = 'https://creator.douyin.com';

export interface EcommerceAccountBrowserState {
  cookieByOrigin?: Record<string, string>;
  cookie_by_origin?: Record<string, string>;
  localStorageByOrigin?: Record<string, string>;
  local_storage_by_origin?: Record<string, string>;
}

export interface SubmitEcommerceAccountAuthPayload {
  platformType: EcommercePlatformType;
  accountType?: EcommerceAccountType;
  businessType?: EcommerceBusinessType;
  shopId?: string;
  linkedDouyinAccountId?: string;
  cookie: string;
  browserState?: EcommerceAccountBrowserState;
}

export interface EcommerceAccountAuthRecord {
  ecom_account_id: string;
  user_id: string;
  platform_type: EcommercePlatformType;
  account_type?: EcommerceAccountType | string;
  business_type?: EcommerceBusinessType | string;
  platform_account_uid: string;
  platform_account_name: string;
  platform_avatar_url: string;
  linked_shop_id?: string;
  linked_shop_ecom_account_id?: string;
  linked_shop_name?: string;
  auth_status: KnownEcommerceAuthStatus | string;
  session_status?: EcommerceAccountSessionStatus | string;
  session_status_reason?: string;
  session_status_message?: string;
  session_expired_at?: string | null;
  session_status_update_time?: string | null;
  requires_relogin?: boolean;
  auth_time: string;
  create_time: string;
  update_time: string;
  productSyncTask?: EcommerceAccountSyncTask | null;
  autoCollectTasks?: EcommerceAccountSyncTask[] | null;
  is_demo?: boolean;
}

export interface EcommerceAccountDetail extends EcommerceAccountAuthRecord {
  cookie: string;
  browserState?: EcommerceAccountBrowserState;
  browser_state?: EcommerceAccountBrowserState;
}

export interface EcommerceAccountSyncTask {
  taskId: string;
  batchId?: string;
  taskType?: string;
  taskTypeLabel?: string;
  status?: string;
  sourceType?: string;
  targetKey?: string;
}

export interface ListEcommerceAccountsParams {
  accountType?: EcommerceAccountType;
  linkedShopId?: string;
}

export interface EcommerceShopContext extends EcommerceAccountAuthRecord {
  shop_id?: string;
  douyin_accounts?: EcommerceAccountAuthRecord[];
}

export type CreatorOverviewTimeRange = '1d' | '7d' | '30d';
export type CreatorOverviewPlatformType = 'douyin';
export type ShopOverviewTimeRange = 'today' | '1d' | '7d' | '30d' | 'natural_week';
export type ShopOverviewTab = 'trade' | 'settlement' | 'refund' | 'traffic';
export type ShopOverviewMetricFormatType = 'amount' | 'count' | 'percent';
export type ShowcaseOverviewTab = 'showcase' | 'audience';

export interface CreatorOverviewQuery {
  platformType?: CreatorOverviewPlatformType;
  ecomAccountId?: string;
  timeRange?: CreatorOverviewTimeRange | string;
}

export interface CreatorOverviewPayload {
  platformType: CreatorOverviewPlatformType | string;
  timeRange: CreatorOverviewTimeRange | string;
  baselineDate?: string;
  targetDate?: string;
  snapshotDate?: string;
  accountCount: number;
  ecomAccountIds: string[];
  publishedVideoCount: number;
  publishedVideoLikeCount: number;
  publishedVideoLikeIncrease: number;
  publishedVideoCommentCount: number;
  publishedVideoCommentIncrease: number;
  followers: number;
}

export interface ShopOverviewQuery {
  shopId: string;
  timeRange?: ShopOverviewTimeRange;
  beginDate?: string;
  endDate?: string;
  dateType?: number;
  tab?: ShopOverviewTab;
  metric?: string;
  refresh?: boolean;
}

export interface ShopOverviewMetric {
  index_name: string;
  label: string;
  format_type: ShopOverviewMetricFormatType | string;
  summary_label: string;
  compare_label: string;
  value: number | null;
  value_text: string;
  last_value: number | null;
  last_value_text: string;
  last_period_change: number | null;
  last_period_change_text: string;
  trend: 'up' | 'down' | 'flat' | string;
}

export interface ShopOverviewTrendPoint {
  x: string;
  x_timestamp?: number | null;
  y: number | null;
}

export interface ShopOverviewTrend {
  index_name: string;
  label: string;
  format_type: ShopOverviewMetricFormatType | string;
  compare_label: string;
  points: ShopOverviewTrendPoint[];
  benchmark_label?: string;
  benchmark_points?: ShopOverviewTrendPoint[];
}

export interface ShopOverviewResponse {
  shop: {
    ecom_account_id: string;
    shop_id: string;
    shop_name: string;
    platform_avatar_url?: string;
    session_status?: string;
    session_status_message?: string;
    business_type?: string;
  };
  time_range: {
    value: ShopOverviewTimeRange | string;
    label: string;
    begin_date: string;
    end_date: string;
    date_type: number;
    summary_label: string;
    compare_label: string;
  };
  tab: {
    key: ShopOverviewTab | string;
    label: string;
  };
  active_metric: ShopOverviewMetric;
  summary_metrics: ShopOverviewMetric[];
  trend: ShopOverviewTrend;
  cache: {
    ttl_seconds: number;
    core_hit: boolean;
    trend_hit: boolean;
    refreshed_at: string;
  };
  auth_fallback?: boolean;
  auth_fallback_reason?: string;
}

export interface ShowcaseOverviewQuery {
  shopId: string;
  beginDate: string;
  endDate: string;
  dateType: number;
  dataType?: number;
  tab?: ShowcaseOverviewTab | string;
  selectedIndex?: string;
  refresh?: boolean;
}

export interface ShowcaseOverviewMetric {
  data_type: number;
  label: string;
  format_type: ShopOverviewMetricFormatType | string;
  summary_label: string;
  compare_label: string;
  value: number | null;
  value_text: string;
  last_value: number | null;
  last_value_text: string;
  last_period_change?: number | null;
  last_period_change_text?: string;
  trend: 'up' | 'down' | 'flat' | string;
}

export interface ShowcaseOverviewTrendPoint {
  point_name?: string;
  x: string;
  y: number | null;
}

export interface ShowcaseOverviewResponse {
  shop: {
    ecom_account_id: string;
    shop_id: string;
    shop_name: string;
    platform_avatar_url?: string;
    session_status?: string;
    session_status_message?: string;
    business_type?: string;
  };
  time_range: {
    value: ShopOverviewTimeRange | string;
    label: string;
    begin_date: string;
    end_date: string;
    date_type: number;
    summary_label: string;
    compare_label: string;
  };
  tab: {
    key: ShowcaseOverviewTab | string;
    label: string;
  };
  active_metric: ShowcaseOverviewMetric;
  summary_metrics: ShowcaseOverviewMetric[];
  trend: {
    data_type: number;
    label: string;
    format_type: ShopOverviewMetricFormatType | string;
    compare_label: string;
    points: ShowcaseOverviewTrendPoint[];
    unit?: string;
  };
  selected_index?: string;
  audience_options?: Array<{
    key: string;
    label: string;
    display: string;
    selected: boolean;
  }>;
  audience_trend?: {
    data_type: number;
    selected_index: string;
    lines: Array<{
      key: string;
      label: string;
      display: string;
      unit?: string;
      points: ShowcaseOverviewTrendPoint[];
    }>;
  };
  cache: {
    ttl_seconds: number;
    core_hit: boolean;
    trend_hit: boolean;
    refreshed_at: string;
  };
}

const SUBMIT_ECOMMERCE_ACCOUNT_AUTH_DEBOUNCE_MS = 500;

interface PendingEcommerceAccountAuthRequest {
  timerId: number | null;
  latestCallId: number;
  resolve?: (value: EcommerceAccountAuthRecord | null) => void;
  reject?: (reason?: unknown) => void;
}

const pendingEcommerceAccountAuthRequests = new Map<string, PendingEcommerceAccountAuthRequest>();

function buildEcommerceAuthRequestKey(payload: SubmitEcommerceAccountAuthPayload) {
  return [
    payload.platformType,
    payload.accountType || 'SHOP',
    payload.businessType || '',
    payload.shopId || '',
  ].join(':');
}

export const submitEcommerceAccountAuth = (payload: SubmitEcommerceAccountAuthPayload) => {
  const requestKey = buildEcommerceAuthRequestKey(payload);
  const currentPendingRequest = pendingEcommerceAccountAuthRequests.get(requestKey) ?? {
    timerId: null,
    latestCallId: 0,
  };
  const nextCallId = currentPendingRequest.latestCallId + 1;

  if (typeof currentPendingRequest.timerId === 'number') {
    window.clearTimeout(currentPendingRequest.timerId);
  }

  currentPendingRequest.resolve?.(null);

  return new Promise<EcommerceAccountAuthRecord | null>((resolve, reject) => {
    currentPendingRequest.latestCallId = nextCallId;
    currentPendingRequest.resolve = resolve;
    currentPendingRequest.reject = reject;
    currentPendingRequest.timerId = window.setTimeout(() => {
      currentPendingRequest.timerId = null;

      void http.post<EcommerceAccountAuthRecord>(API_URLS.ecommerceAccountAuth, payload)
        .then((record) => {
          if (currentPendingRequest.latestCallId !== nextCallId) {
            resolve(null);
            return;
          }

          resolve(record);
        })
        .catch((error) => {
          if (currentPendingRequest.latestCallId !== nextCallId) {
            resolve(null);
            return;
          }

          reject(error);
        })
        .finally(() => {
          if (currentPendingRequest.latestCallId === nextCallId) {
            pendingEcommerceAccountAuthRequests.delete(requestKey);
          }
        });
    }, SUBMIT_ECOMMERCE_ACCOUNT_AUTH_DEBOUNCE_MS);

    pendingEcommerceAccountAuthRequests.set(requestKey, currentPendingRequest);
  });
};

export const fetchEcommerceAccounts = (params: ListEcommerceAccountsParams = {}) => {
  const query: Record<string, string> = {};
  if (params.accountType) {
    query.accountType = params.accountType;
  }
  if (params.linkedShopId) {
    query.linkedShopId = params.linkedShopId;
  }
  return http.get<EcommerceAccountAuthRecord[]>(API_URLS.ecommerceAccounts, {
    params: Object.keys(query).length > 0 ? query : undefined,
  });
};

export const fetchEcommerceShopContexts = () => {
  return http.get<EcommerceShopContext[]>(`${API_URLS.ecommerceAccounts}/shop-contexts`);
};

export const fetchEcommerceAccountDetail = (ecomAccountId: string) => {
  return http.get<EcommerceAccountDetail>(`${API_URLS.ecommerceAccounts}/${ecomAccountId}`);
};

export const submitEcommerceAccountProductSync = (ecomAccountId: string) => {
  return http.post<EcommerceAccountSyncTask>(API_URLS.ecommerceAccountSyncProducts(ecomAccountId));
};

export const fetchCreatorOverview = (params: CreatorOverviewQuery = {}) => {
  return http.get<CreatorOverviewPayload>(API_URLS.creatorOverview, { params });
};

export const fetchEcommerceShopOverview = (params: ShopOverviewQuery) => {
  return http.get<ShopOverviewResponse>(API_URLS.shopOverview, { params });
};

export const fetchEcommerceShowcaseOverview = (params: ShowcaseOverviewQuery) => {
  return http.get<ShowcaseOverviewResponse>(API_URLS.showcaseOverview, { params });
};

export const unbindEcommerceAccount = (ecomAccountId: string) => {
  return http.delete<null>(`${API_URLS.ecommerceAccounts}/${ecomAccountId}`);
};

export const linkExistingDouyinAccount = (ecomAccountId: string, shopId: string) => {
  return http.post<EcommerceAccountAuthRecord>(
    `${API_URLS.ecommerceAccounts}/${ecomAccountId}/link-shop`,
    { shopId }
  );
};

export const detectEcommerceAccountSessionStatus = (ecomAccountId: string) => {
  return http.post<EcommerceAccountAuthRecord>(
    API_URLS.ecommerceAccountDetectSession(ecomAccountId)
  );
};

export const updateEcommerceAccountSessionStatus = (
  ecomAccountId: string,
  payload: {
    sessionStatus: EcommerceAccountSessionStatus;
    message?: string;
  }
) => {
  return http.post<EcommerceAccountAuthRecord>(
    API_URLS.ecommerceAccountSessionStatus(ecomAccountId),
    payload
  );
};

export const updateAdminEcommerceAccountSessionStatus = updateEcommerceAccountSessionStatus;
